import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import SuperadminLayout from './SuperadminLayout';
import { Modal, Button } from 'react-bootstrap';
import axios from '../../axios/axiosconf';
import { ordenarListaAlfabetica } from './superadminSortUtils';
import './SuperadminStyles.css';

const WhatsappControl = () => {
    const history = useHistory();
    const [clientes, setClientes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [alert, setAlert] = useState({ show: false, type: '', message: '' });

    // Modal states
    const [showEditModal, setShowEditModal] = useState(false);
    const [showResetModal, setShowResetModal] = useState(false);
    const [showHistorialModal, setShowHistorialModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedCliente, setSelectedCliente] = useState(null);

    // Edit modal state
    const [editLimite, setEditLimite] = useState(0);
    const [editError, setEditError] = useState('');

    // Create modal state
    const [createLimite, setCreateLimite] = useState(100);
    const [createError, setCreateError] = useState('');

    // Historial state
    const [historial, setHistorial] = useState([]);
    const [historialTotal, setHistorialTotal] = useState(0);
    const [historialPage, setHistorialPage] = useState(1);
    const [historialTotalPages, setHistorialTotalPages] = useState(1);
    const [historialLoading, setHistorialLoading] = useState(false);

    // Debounce search
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        const token = localStorage.getItem('superadmin_token');
        const user = localStorage.getItem('superadmin_user');

        if (!token || !user) {
            history.push('/superadmin/login');
            return;
        }
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        fetchClientes(1, '');
    }, [history]);

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Fetch when debounced search changes
    useEffect(() => {
        setCurrentPage(1);
        fetchClientes(1, debouncedSearch);
    }, [debouncedSearch]);

    const fetchClientes = async (page, search) => {
        try {
            setLoading(true);
            const response = await axios.get(`/superadmin/whatsapp?page=${page}&search=${search}`);
            setClientes(ordenarListaAlfabetica(response.data.data, 'nombre'));
            setCurrentPage(response.data.current_page);
            setTotalPages(response.data.last_page);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching clientes:', error);
            if (error.response && error.response.status === 401) {
                localStorage.removeItem('superadmin_token');
                localStorage.removeItem('superadmin_user');
                history.push('/superadmin/login');
            }
            setLoading(false);
        }
    };

    const showAlertMessage = (type, message) => {
        setAlert({ show: true, type, message });
        setTimeout(() => setAlert({ show: false, type: '', message: '' }), 5000);
    };

    // Get row class based on consumption
    const getRowClass = (cliente) => {
        if (cliente.limite_id === null) return '';
        if (cliente.consumo_actual >= cliente.limite_mensual) return 'table-danger';
        if (cliente.consumo_actual >= 0.8 * cliente.limite_mensual) return 'table-warning';
        return '';
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
            fetchClientes(newPage, debouncedSearch);
        }
    };

    // Edit limit modal handlers
    const handleEditClick = (cliente) => {
        setSelectedCliente(cliente);
        setEditLimite(cliente.limite_mensual || 0);
        setEditError('');
        setShowEditModal(true);
    };

    const handleEditConfirm = async () => {
        const value = parseInt(editLimite, 10);
        if (isNaN(value) || value < 0) {
            setEditError('El límite debe ser un número entero mayor o igual a 0');
            return;
        }

        try {
            await axios.put(`/superadmin/whatsapp/${selectedCliente.client_id}/limite`, {
                limite_mensual: value
            });
            setShowEditModal(false);
            fetchClientes(currentPage, debouncedSearch);
            showAlertMessage('success', 'Límite actualizado correctamente');
        } catch (error) {
            console.error('Error updating limite:', error);
            showAlertMessage('danger', error.response?.data?.message || 'Error al actualizar el límite');
        }
    };

    // Reset consumption modal handlers
    const handleResetClick = (cliente) => {
        setSelectedCliente(cliente);
        setShowResetModal(true);
    };

    const handleResetConfirm = async () => {
        try {
            await axios.post(`/superadmin/whatsapp/${selectedCliente.client_id}/resetear`);
            setShowResetModal(false);
            fetchClientes(currentPage, debouncedSearch);
            showAlertMessage('success', 'Consumo reseteado correctamente');
        } catch (error) {
            console.error('Error resetting consumo:', error);
            showAlertMessage('danger', error.response?.data?.message || 'Error al resetear el consumo');
        }
    };

    // Historial modal handlers
    const handleHistorialClick = async (cliente) => {
        setSelectedCliente(cliente);
        setHistorialPage(1);
        setShowHistorialModal(true);
        await fetchHistorial(cliente.client_id, 1);
    };

    const fetchHistorial = async (clientId, page) => {
        try {
            setHistorialLoading(true);
            const response = await axios.get(`/superadmin/whatsapp/${clientId}/historial?page=${page}`);
            setHistorial(response.data.data);
            setHistorialTotal(response.data.total);
            setHistorialPage(response.data.current_page);
            setHistorialTotalPages(response.data.last_page);
            setHistorialLoading(false);
        } catch (error) {
            console.error('Error fetching historial:', error);
            setHistorialLoading(false);
        }
    };

    const handleHistorialPageChange = (newPage) => {
        if (newPage >= 1 && newPage <= historialTotalPages && selectedCliente) {
            setHistorialPage(newPage);
            fetchHistorial(selectedCliente.client_id, newPage);
        }
    };

    // Toggle active/inactive handler
    const handleToggleClick = async (cliente) => {
        try {
            const response = await axios.put(`/superadmin/whatsapp/${cliente.client_id}/toggle`);
            fetchClientes(currentPage, debouncedSearch);
            const nuevoEstado = response.data.limite?.activo ? 'Activo' : 'Inactivo';
            showAlertMessage('success', `Estado cambiado a: ${nuevoEstado}`);
        } catch (error) {
            console.error('Error toggling estado:', error);
            showAlertMessage('danger', error.response?.data?.message || 'Error al cambiar el estado');
        }
    };

    // Create limit modal handlers
    const handleCreateClick = (cliente) => {
        setSelectedCliente(cliente);
        setCreateLimite(100);
        setCreateError('');
        setShowCreateModal(true);
    };

    const handleCreateConfirm = async () => {
        const value = parseInt(createLimite, 10);
        if (isNaN(value) || value < 0) {
            setCreateError('El límite debe ser un número entero mayor o igual a 0');
            return;
        }

        try {
            await axios.post('/superadmin/whatsapp', {
                client_id: selectedCliente.client_id,
                limite_mensual: value
            });
            setShowCreateModal(false);
            fetchClientes(currentPage, debouncedSearch);
            showAlertMessage('success', 'WhatsApp configurado correctamente');
        } catch (error) {
            console.error('Error creating limite:', error);
            showAlertMessage('danger', error.response?.data?.message || 'Error al configurar WhatsApp');
        }
    };

    // Truncate message for historial
    const truncateMessage = (message, maxLength = 100) => {
        if (!message) return '';
        if (message.length <= maxLength) return message;
        return message.substring(0, maxLength) + '...';
    };

    // Format date for historial
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    };

    // Generate pagination numbers
    const getPaginationNumbers = (current, total) => {
        const pages = [];
        const maxVisible = 5;
        let start = Math.max(1, current - Math.floor(maxVisible / 2));
        let end = Math.min(total, start + maxVisible - 1);
        
        if (end - start + 1 < maxVisible) {
            start = Math.max(1, end - maxVisible + 1);
        }

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }
        return pages;
    };

    if (loading && clientes.length === 0) {
        return (
            <SuperadminLayout>
                <div className="loading-container">
                    <div className="spinner-border text-info" role="status">
                        <span className="sr-only">Cargando...</span>
                    </div>
                </div>
            </SuperadminLayout>
        );
    }

    return (
        <SuperadminLayout>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4><i className="fab fa-whatsapp"></i> Control de WhatsApp</h4>
            </div>

            {/* Alertas */}
            {alert.show && (
                <div className={`alert alert-${alert.type} alert-dismissible fade show`} role="alert">
                    {alert.message}
                    <button type="button" className="close" onClick={() => setAlert({ show: false, type: '', message: '' })}>
                        <span>&times;</span>
                    </button>
                </div>
            )}

            {/* Búsqueda */}
            <div className="row mb-3">
                <div className="col-md-6">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Buscar por nombre de cliente..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Tabla */}
            <div className="table-responsive">
                <table className="table table-hover superadmin-table">
                    <thead>
                        <tr>
                            <th>Nombre Cliente</th>
                            <th>Habilitados</th>
                            <th>Consumidos</th>
                            <th>Estado</th>
                            <th>Opciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {clientes.length > 0 ? (
                            clientes.map(cliente => (
                                <tr key={cliente.client_id} className={getRowClass(cliente)}>
                                    <td>
                                        {cliente.nombre}
                                        {cliente.limite_id === null && (
                                            <span className="badge badge-secondary ml-2">Sin configurar</span>
                                        )}
                                    </td>
                                    <td>
                                        {cliente.limite_id !== null ? cliente.limite_mensual : '-'}
                                    </td>
                                    <td>
                                        {cliente.limite_id !== null ? cliente.consumo_actual : '-'}
                                    </td>
                                    <td>
                                        {cliente.limite_id !== null ? (
                                            cliente.activo ? (
                                                <span className="badge badge-success">Activo</span>
                                            ) : (
                                                <span className="badge badge-secondary">Inactivo</span>
                                            )
                                        ) : (
                                            '-'
                                        )}
                                    </td>
                                    <td>
                                        {cliente.limite_id !== null ? (
                                            <>
                                                <button
                                                    className="btn btn-sm btn-dark mr-1"
                                                    onClick={() => handleEditClick(cliente)}
                                                    title="Editar límite"
                                                >
                                                    <i className="fas fa-edit"></i>
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-warning mr-1"
                                                    onClick={() => handleResetClick(cliente)}
                                                    title="Resetear consumo"
                                                >
                                                    <i className="fas fa-redo"></i>
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-info mr-1"
                                                    onClick={() => handleHistorialClick(cliente)}
                                                    title="Ver historial"
                                                >
                                                    <i className="fas fa-history"></i>
                                                </button>
                                                <button
                                                    className={`btn btn-sm ${cliente.activo ? 'btn-success' : 'btn-secondary'}`}
                                                    onClick={() => handleToggleClick(cliente)}
                                                    title={cliente.activo ? 'Desactivar' : 'Activar'}
                                                >
                                                    <i className={`fas ${cliente.activo ? 'fa-toggle-on' : 'fa-toggle-off'}`}></i>
                                                </button>
                                            </>
                                        ) : (
                                            <button
                                                className="btn btn-sm btn-success"
                                                onClick={() => handleCreateClick(cliente)}
                                                title="Configurar WhatsApp"
                                            >
                                                <i className="fas fa-cog"></i> Configurar
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="5" className="text-center">
                                    No se encontraron clientes
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Paginación */}
            {totalPages > 1 && (
                <nav>
                    <ul className="pagination justify-content-center">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button
                                className="page-link"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                            >
                                Anterior
                            </button>
                        </li>
                        {getPaginationNumbers(currentPage, totalPages).map(page => (
                            <li
                                key={page}
                                className={`page-item ${currentPage === page ? 'active' : ''}`}
                            >
                                <button
                                    className="page-link"
                                    onClick={() => handlePageChange(page)}
                                >
                                    {page}
                                </button>
                            </li>
                        ))}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button
                                className="page-link"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                            >
                                Siguiente
                            </button>
                        </li>
                    </ul>
                </nav>
            )}

            {/* Modal Editar Límite */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Editar Límite de Mensajes</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Cliente: <strong>{selectedCliente?.nombre}</strong></p>
                    <div className="form-group">
                        <label>Límite mensual de mensajes:</label>
                        <input
                            type="number"
                            className={`form-control ${editError ? 'is-invalid' : ''}`}
                            min="0"
                            value={editLimite}
                            onChange={(e) => {
                                setEditLimite(e.target.value);
                                setEditError('');
                            }}
                        />
                        {editError && <div className="invalid-feedback">{editError}</div>}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                        Cancelar
                    </Button>
                    <Button variant="dark" onClick={handleEditConfirm}>
                        Guardar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal Resetear Consumo */}
            <Modal show={showResetModal} onHide={() => setShowResetModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Resetear Consumo</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    ¿Está seguro que desea resetear el consumo del cliente{' '}
                    <strong>{selectedCliente?.nombre}</strong>?
                    <br /><br />
                    Consumo actual: <strong>{selectedCliente?.consumo_actual}</strong>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowResetModal(false)}>
                        Cancelar
                    </Button>
                    <Button variant="warning" onClick={handleResetConfirm}>
                        Resetear
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal Historial */}
            <Modal show={showHistorialModal} onHide={() => setShowHistorialModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Historial de Envíos - {selectedCliente?.nombre}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p><strong>Total de mensajes: {historialTotal}</strong></p>
                    
                    {historialLoading ? (
                        <div className="text-center">
                            <div className="spinner-border text-info" role="status">
                                <span className="sr-only">Cargando...</span>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <table className="table table-sm table-hover">
                                    <thead>
                                        <tr>
                                            <th>Destinatario</th>
                                            <th>Mensaje</th>
                                            <th>Estado</th>
                                            <th>Proveedor</th>
                                            <th>Fecha</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {historial.length > 0 ? (
                                            historial.map((log, index) => (
                                                <tr key={log.id || index}>
                                                    <td>{log.recipient}</td>
                                                    <td title={log.message}>{truncateMessage(log.message)}</td>
                                                    <td>
                                                        <span className={`badge badge-${log.status === 'sent' || log.status === 'delivered' ? 'success' : log.status === 'failed' ? 'danger' : 'secondary'}`}>
                                                            {log.status}
                                                        </span>
                                                    </td>
                                                    <td>{log.provider}</td>
                                                    <td>{formatDate(log.created_at)}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="5" className="text-center">
                                                    No hay registros de envío
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Paginación del historial */}
                            {historialTotalPages > 1 && (
                                <nav>
                                    <ul className="pagination justify-content-center pagination-sm">
                                        <li className={`page-item ${historialPage === 1 ? 'disabled' : ''}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => handleHistorialPageChange(historialPage - 1)}
                                                disabled={historialPage === 1}
                                            >
                                                Anterior
                                            </button>
                                        </li>
                                        {getPaginationNumbers(historialPage, historialTotalPages).map(page => (
                                            <li
                                                key={page}
                                                className={`page-item ${historialPage === page ? 'active' : ''}`}
                                            >
                                                <button
                                                    className="page-link"
                                                    onClick={() => handleHistorialPageChange(page)}
                                                >
                                                    {page}
                                                </button>
                                            </li>
                                        ))}
                                        <li className={`page-item ${historialPage === historialTotalPages ? 'disabled' : ''}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => handleHistorialPageChange(historialPage + 1)}
                                                disabled={historialPage === historialTotalPages}
                                            >
                                                Siguiente
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowHistorialModal(false)}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal Crear Límite */}
            <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Configurar WhatsApp</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Configurar límite de mensajes para <strong>{selectedCliente?.nombre}</strong></p>
                    <div className="form-group">
                        <label>Límite mensual de mensajes:</label>
                        <input
                            type="number"
                            className={`form-control ${createError ? 'is-invalid' : ''}`}
                            min="0"
                            value={createLimite}
                            onChange={(e) => {
                                setCreateLimite(e.target.value);
                                setCreateError('');
                            }}
                        />
                        {createError && <div className="invalid-feedback">{createError}</div>}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
                        Cancelar
                    </Button>
                    <Button variant="success" onClick={handleCreateConfirm}>
                        Configurar
                    </Button>
                </Modal.Footer>
            </Modal>
        </SuperadminLayout>
    );
};

export default WhatsappControl;
